import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Circle, Popup, Polyline, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { Sparkles, Loader2, MapPin, Clock, Route as RouteIcon, ChevronRight, ArrowRight, Save, Compass, X, Plus, Bike, Footprints, Car, Bus, Search, Pencil, Trash2, RefreshCw, Download } from "lucide-react";
import { useLang } from "../LangContext";
import { useTenant } from "../TenantContext";
import { useAuth, adminAxios } from "../AuthContext";
import { StopMediaEditor, StopMediaGallery, Panorama360Viewer } from "../components/StopMedia";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pinIcon = new L.DivIcon({
    className: "itin-center-pin",
    html: `<svg width="30" height="42" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg"><path d="M15 0C6.7 0 0 6.7 0 15c0 11 13.6 25 14.1 25.6a1.3 1.3 0 0 0 1.8 0C16.4 40 30 26 30 15 30 6.7 23.3 0 15 0z" fill="#d97706" stroke="white" stroke-width="2"/><circle cx="15" cy="15" r="5.5" fill="white"/></svg>`,
    iconSize: [30, 42], iconAnchor: [15, 42],
});

// Same day-colored numbered pin as detail page
const dayColor = (n) => ["#059669", "#d97706", "#7c3aed", "#e11d48", "#0284c7", "#65a30d", "#f59e0b"][n % 7];
const stopIcon = (n, day) => new L.DivIcon({
    className: "itin-stop-pin",
    html: `<div style="background:${dayColor((day || 1) - 1)};color:white;width:30px;height:30px;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;font-family:sans-serif;">${n}</div>`,
    iconSize: [30, 30], iconAnchor: [15, 15],
});

const MOBILITY_ICON = { "a-piedi": Footprints, "bici": Bike, "moto": Car, "auto": Car, "pulman": Bus, "misto": Compass };

function MapClickPicker({ onPick }) {
    useMapEvents({
        click(e) { onPick(e.latlng); },
    });
    return null;
}

const stepLabels = {
    it: ["Interessi", "Durata", "Mobilità", "Zona", "Periodo & Eventi"],
    en: ["Interests", "Duration", "Mobility", "Area", "Dates & Events"],
    fr: ["Intérêts", "Durée", "Mobilité", "Zone", "Dates & Événements"],
    es: ["Intereses", "Duración", "Movilidad", "Zona", "Fechas y Eventos"],
    de: ["Interessen", "Dauer", "Mobilität", "Bereich", "Zeitraum & Events"],
};

const uiText = {
    it: { title:"Itinerari AI",sub:"Crea itinerari personalizzati con l'intelligenza artificiale.",cta:"Nuovo Itinerario",wizard_title:"Componi il tuo itinerario",gen:"Genera con AI",generating:"Sto costruendo l'itinerario...",no_itin:"Ancora nessun itinerario per questo borgo — crea il primo!",days_lbl:"Durata (giorni)",radius:"Raggio (km)",include_borgo:"Includi il borgo di partenza",location_lbl:"Località di riferimento (fino a 4)",search:"Aggiungi una località (es. Nuoro, Cagliari, Bosa)…",save:"Salva itinerario",saved:"Itinerario salvato!",next:"Avanti",back:"Indietro",skip:"Salta",saved_itin:"Itinerari salvati",view:"Vedi",delete:"Elimina",add:"Aggiungi",remove:"Rimuovi",primary:"Principale",period_lbl:"Periodo del viaggio (opzionale)",date_from:"Dal",date_to:"Al",events_toggle:"Intercetta sagre, feste patronali ed eventi religiosi in queste date",events_hint:"L'AI cercherà di inserire eventi tradizionali che cadono nelle date scelte" },
    en: { title:"AI Itineraries",sub:"Create custom itineraries with AI.",cta:"New Itinerary",wizard_title:"Compose your itinerary",gen:"Generate with AI",generating:"Building your itinerary...",no_itin:"No itineraries for this village yet — create the first!",days_lbl:"Duration (days)",radius:"Radius (km)",include_borgo:"Include starting village",location_lbl:"Reference locations (up to 4)",search:"Add a location (e.g. Nuoro, Cagliari, Bosa)…",save:"Save itinerary",saved:"Itinerary saved!",next:"Next",back:"Back",skip:"Skip",saved_itin:"Saved itineraries",view:"View",delete:"Delete",add:"Add",remove:"Remove",primary:"Primary",period_lbl:"Travel period (optional)",date_from:"From",date_to:"To",events_toggle:"Intercept festivals, patron feasts and religious events in these dates",events_hint:"AI will try to include traditional events matching these dates" },
    fr: { title:"Itinéraires IA",sub:"Créez des itinéraires personnalisés avec l'IA.",cta:"Nouvel Itinéraire",wizard_title:"Composez votre itinéraire",gen:"Générer avec l'IA",generating:"Création en cours...",no_itin:"Aucun itinéraire pour ce village — créez le premier !",days_lbl:"Durée (jours)",radius:"Rayon (km)",include_borgo:"Inclure le village de départ",location_lbl:"Lieux de référence (jusqu'à 4)",search:"Ajouter un lieu (ex. Nuoro, Cagliari, Bosa)…",save:"Enregistrer",saved:"Itinéraire enregistré !",next:"Suivant",back:"Retour",skip:"Passer",saved_itin:"Itinéraires enregistrés",view:"Voir",delete:"Supprimer",add:"Ajouter",remove:"Supprimer",primary:"Principal",period_lbl:"Période du voyage (optionnel)",date_from:"Du",date_to:"Au",events_toggle:"Intercepter fêtes patronales, foires et événements religieux à ces dates",events_hint:"L'IA essaiera d'inclure les événements traditionnels correspondants" },
    es: { title:"Itinerarios IA",sub:"Crea itinerarios personalizados con IA.",cta:"Nuevo Itinerario",wizard_title:"Componer tu itinerario",gen:"Generar con IA",generating:"Creando itinerario...",no_itin:"Aún no hay itinerarios — ¡crea el primero!",days_lbl:"Duración (días)",radius:"Radio (km)",include_borgo:"Incluir el pueblo de partida",location_lbl:"Lugares de referencia (hasta 4)",search:"Añadir un lugar (ej. Nuoro, Cagliari, Bosa)…",save:"Guardar itinerario",saved:"¡Itinerario guardado!",next:"Siguiente",back:"Atrás",skip:"Omitir",saved_itin:"Itinerarios guardados",view:"Ver",delete:"Eliminar",add:"Añadir",remove:"Quitar",primary:"Principal",period_lbl:"Periodo del viaje (opcional)",date_from:"Desde",date_to:"Hasta",events_toggle:"Interceptar fiestas patronales, ferias y eventos religiosos en estas fechas",events_hint:"La IA intentará incluir eventos tradicionales que coincidan" },
    de: { title:"KI-Routen",sub:"Erstellen Sie individuelle Routen mit KI.",cta:"Neue Route",wizard_title:"Route zusammenstellen",gen:"Mit KI generieren",generating:"Route wird erstellt...",no_itin:"Noch keine Routen — erstellen Sie die erste!",days_lbl:"Dauer (Tage)",radius:"Radius (km)",include_borgo:"Startdorf einbeziehen",location_lbl:"Referenzorte (bis zu 4)",search:"Ort hinzufügen (z.B. Nuoro, Cagliari, Bosa)…",save:"Route speichern",saved:"Route gespeichert!",next:"Weiter",back:"Zurück",skip:"Überspringen",saved_itin:"Gespeicherte Routen",view:"Ansehen",delete:"Löschen",add:"Hinzufügen",remove:"Entfernen",primary:"Haupt",period_lbl:"Reisezeitraum (optional)",date_from:"Von",date_to:"Bis",events_toggle:"Feste, Volksfeste und religiöse Events in diesen Daten einbeziehen",events_hint:"KI wird versuchen, passende traditionelle Events einzubauen" },
};

export default function ItinerariPage() {
    const { lang } = useLang();
    const { slug, tenant } = useTenant();
    const { user } = useAuth();
    const t = uiText[lang] || uiText.it;

    const [catalog, setCatalog] = useState({ interests: [], mobility: [] });
    const [list, setList] = useState([]);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [editing, setEditing] = useState(null);   // itinerary being edited
    const [busyId, setBusyId] = useState("");

    const isAdmin = !!user && (user.role === "super_admin" || user.role === "borgo_admin" || user.role === "admin");
    // borgo_admin can only manage itineraries of its own tenant
    const canManage = (it) => user?.role === "super_admin" || (isAdmin && it.tenant_id === user?.tenant_id);

    useEffect(() => {
        axios.get(`${API}/itineraries/catalog`).then((r) => setCatalog(r.data)).catch(() => {});
    }, []);

    const loadList = () => {
        axios.get(`${API}/itineraries`, { params: { borgo: slug } })
            .then((r) => setList(r.data || []))
            .catch(() => setList([]));
    };
    useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [slug]);

    // Auto-open editor when redirected from detail page with ?edit=<id>
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const editId = params.get("edit");
        if (editId && list.length > 0) {
            const target = list.find((it) => it.id === editId || it.share_slug === editId);
            if (target && !editing) setEditing(target);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list]);

    const handleDelete = async (it) => {
        if (!window.confirm(`Eliminare definitivamente "${it.title}"?`)) return;
        setBusyId(it.id);
        try {
            await adminAxios().delete(`/itineraries/${it.id}`);
            loadList();
        } catch (e) {
            window.alert(e?.response?.data?.detail || "Errore eliminazione");
        } finally { setBusyId(""); }
    };

    return (
        <div className="min-h-screen bg-stone-50">
            <div className="pt-28 pb-16 px-6 lg:px-10">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.3em] text-amber-700 mb-3">
                            <Sparkles size={14} /> AI · {tenant?.name || "Borgo"}
                        </div>
                        <h1 className="font-serif text-4xl md:text-5xl font-semibold text-stone-900" data-testid="itin-page-title">{t.title}</h1>
                        <p className="mt-3 text-stone-600 max-w-2xl mx-auto">{t.sub}</p>
                        <button
                            onClick={() => setWizardOpen(true)}
                            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-semibold text-sm shadow"
                            data-testid="itin-new-cta"
                        >
                            <Plus size={16} /> {t.cta}
                        </button>
                    </div>

                    <div className="mb-6">
                        <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-3">{t.saved_itin}</div>
                        {list.length === 0 ? (
                            <div className="rounded-3xl border-2 border-dashed border-stone-200 py-14 text-center text-stone-500" data-testid="itin-empty">
                                <Compass size={30} className="mx-auto mb-3 text-stone-400" />
                                {t.no_itin}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="itin-list">
                                {list.map((it) => (
                                    <div
                                        key={it.id}
                                        className="relative bg-white rounded-2xl border border-stone-200 hover:border-emerald-400 shadow-sm hover:shadow-md transition-all group"
                                        data-testid={`itin-card-${it.id}`}
                                    >
                                        {isAdmin && canManage(it) && (
                                            <div className="absolute top-2 right-2 flex gap-1 z-10">
                                                <a
                                                    href={`${API}/itineraries/${it.id}/pdf`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="w-8 h-8 rounded-full bg-white/90 hover:bg-stone-100 text-stone-700 border border-stone-200 flex items-center justify-center shadow-sm"
                                                    title="Scarica PDF"
                                                    data-testid={`itin-pdf-${it.id}`}
                                                ><Download size={13} /></a>
                                                <button
                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(it); }}
                                                    className="w-8 h-8 rounded-full bg-white/90 hover:bg-amber-100 text-amber-700 border border-amber-200 flex items-center justify-center shadow-sm"
                                                    title="Modifica"
                                                    data-testid={`itin-edit-${it.id}`}
                                                ><Pencil size={13} /></button>
                                                <button
                                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(it); }}
                                                    disabled={busyId === it.id}
                                                    className="w-8 h-8 rounded-full bg-white/90 hover:bg-red-100 text-red-700 border border-red-200 flex items-center justify-center shadow-sm disabled:opacity-50"
                                                    title="Elimina"
                                                    data-testid={`itin-delete-${it.id}`}
                                                >{busyId === it.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}</button>
                                            </div>
                                        )}
                                        <Link
                                            to={`/itinerari/${it.share_slug || it.id}?borgo=${slug}`}
                                            className="block p-5"
                                        >
                                            <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-1.5">
                                                {it.filters?.days || 1} {(it.filters?.days || 1) === 1 ? "giorno" : "giorni"} · {it.filters?.mobility || "auto"}
                                            </div>
                                            <div className="font-serif text-lg font-semibold text-stone-900 leading-snug mb-2 group-hover:text-emerald-700 transition-colors pr-16">{it.title}</div>
                                            {it.subtitle && <div className="text-sm text-stone-600 line-clamp-2 mb-3">{it.subtitle}</div>}
                                            <div className="flex flex-wrap gap-1 mb-3">
                                                {(it.filters?.interests || []).slice(0, 3).map((iv) => (
                                                    <span key={iv} className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{iv}</span>
                                                ))}
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-stone-500">
                                                <span>{(it.days || []).length} tappe · {(it.days || []).reduce((a, d) => a + (d.stops?.length || 0), 0)} POI</span>
                                                <span className="text-emerald-700 font-semibold group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-1">{t.view} <ChevronRight size={13} /></span>
                                            </div>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {wizardOpen && (
                <ItineraryWizard
                    onClose={() => setWizardOpen(false)}
                    onSaved={() => { setWizardOpen(false); loadList(); }}
                    catalog={catalog}
                    slug={slug}
                    tenant={tenant}
                    t={t}
                    lang={lang}
                />
            )}

            {editing && (
                <ItineraryEditor
                    itinerary={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); loadList(); }}
                />
            )}
        </div>
    );
}

// ============================ WIZARD ============================
const GEN_STEPS = {
    it: [
        "🎯 Analizzando i tuoi interessi...",
        "🗺 Consultando fonti locali e POI del borgo...",
        "🚗 Calcolando percorsi ottimali per la mobilità scelta...",
        "📅 Verificando eventi e sagre nel periodo...",
        "🧭 Componendo giornate e tappe...",
        "🎨 Rifinendo descrizioni e coordinate GPS...",
    ],
    en: ["🎯 Analyzing your interests...", "🗺 Consulting local sources and POIs...", "🚗 Computing optimal routes...", "📅 Checking events and festivals...", "🧭 Composing days and stops...", "🎨 Polishing descriptions and GPS..."],
    fr: ["🎯 Analyse de vos intérêts...", "🗺 Consultation des sources locales...", "🚗 Calcul des itinéraires...", "📅 Vérification des événements...", "🧭 Composition des étapes...", "🎨 Finalisation..."],
    es: ["🎯 Analizando tus intereses...", "🗺 Consultando fuentes locales...", "🚗 Calculando rutas óptimas...", "📅 Verificando eventos...", "🧭 Componiendo etapas...", "🎨 Refinando detalles..."],
    de: ["🎯 Interessen werden analysiert...", "🗺 Lokale Quellen werden abgefragt...", "🚗 Optimale Routen werden berechnet...", "📅 Events werden geprüft...", "🧭 Etappen werden zusammengestellt...", "🎨 Details werden verfeinert..."],
};

function ItineraryWizard({ onClose, onSaved, catalog, slug, tenant, t, lang }) {
    const { user } = useAuth();
    const isAdmin = !!user && (user.role === "super_admin" || user.role === "borgo_admin" || user.role === "admin");
    const [step, setStep] = useState(0);
    const [interests, setInterests] = useState([]);
    const [days, setDays] = useState(2);
    const [mobility, setMobility] = useState("auto");
    // Multi-location: array of {lat, lng, label}. First = primary center. Max 4.
    // include_borgo=true keeps the borgo as chip #0. When false, borgo is EXCLUDED
    // from the chips and the user must have added at least 1 other reference.
    const [borgoLoc] = useState({
        lat: tenant?.location?.lat || 40.0833,
        lng: tenant?.location?.lng || 8.9167,
        label: tenant?.name || "Tadasuni",
    });
    const [extraLocations, setExtraLocations] = useState([]);   // user-added
    const [primaryIdx, setPrimaryIdx] = useState(0);            // index INSIDE the visible chip list
    const [radius, setRadius] = useState(20);
    const [includeBorgo, setIncludeBorgo] = useState(true);
    const [search, setSearch] = useState("");
    const [searchBusy, setSearchBusy] = useState(false);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [includeEvents, setIncludeEvents] = useState(false);
    const [adults, setAdults] = useState(2);
    const [children412, setChildren412] = useState(0);
    const [childrenU4, setChildrenU4] = useState(0);
    const [budgetLevel, setBudgetLevel] = useState("medio");
    const [originCity, setOriginCity] = useState("");
    const [originTransport, setOriginTransport] = useState("auto_ai");
    const [generated, setGenerated] = useState(null);
    const [busy, setBusy] = useState(false);
    const [genStepIdx, setGenStepIdx] = useState(0);
    const [genProgress, setGenProgress] = useState(0);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");
    const [dateWarnOpen, setDateWarnOpen] = useState(false);
    const [suggestedDays, setSuggestedDays] = useState(null);
    // NEW: model selector + chat drawer + accumulated free-form context
    const [modelKey, setModelKey] = useState("claude-sonnet-4-6");
    const [modelList, setModelList] = useState([]);
    const [extraContextList, setExtraContextList] = useState([]);   // [{content, model_used}]
    const [chatOpen, setChatOpen] = useState(false);

    // Load available AI models once
    useEffect(() => {
        axios.get(`${API}/itineraries/models`).then((r) => {
            setModelList(r.data.models || []);
            if (r.data.default) setModelKey(r.data.default);
        }).catch(() => {});
    }, []);

    const steps = stepLabels[lang] || stepLabels.it;
    const genSteps = GEN_STEPS[lang] || GEN_STEPS.it;

    // Compose visible chips list based on includeBorgo
    const visibleLocations = useMemo(() => (
        includeBorgo ? [borgoLoc, ...extraLocations] : [...extraLocations]
    ), [includeBorgo, borgoLoc, extraLocations]);

    // Compute number of days between dateFrom / dateTo (inclusive). Returns null if invalid.
    const periodDays = useMemo(() => {
        if (!dateFrom || !dateTo) return null;
        try {
            const a = new Date(dateFrom); const b = new Date(dateTo);
            if (isNaN(a) || isNaN(b) || b < a) return null;
            return Math.round((b - a) / 86400000) + 1;
        } catch { return null; }
    }, [dateFrom, dateTo]);

    // Called by the "Generate" button. If the selected date range exceeds the
    // number of days chosen at step 2, show a confirmation dialog first.
    const handleGenerateClick = () => {
        if (periodDays && periodDays > days) {
            setSuggestedDays(Math.min(periodDays, 10));
            setDateWarnOpen(true);
            return;
        }
        doGenerate();
    };

    // Clamp primaryIdx when list shrinks
    useEffect(() => {
        if (primaryIdx >= visibleLocations.length) setPrimaryIdx(0);
    }, [visibleLocations.length, primaryIdx]);

    const primary = visibleLocations[primaryIdx] || borgoLoc;

    // Fake progress animation during LLM call (~30-45s)
    useEffect(() => {
        if (!busy) { setGenStepIdx(0); setGenProgress(0); return; }
        let i = 0;
        const stepInterval = setInterval(() => {
            i = Math.min(i + 1, genSteps.length - 1);
            setGenStepIdx(i);
        }, 5500);
        // Progress increment: reach 92% linearly over ~90s (3-day itineraries take longer), then hold
        const startedAt = Date.now();
        const progInterval = setInterval(() => {
            const elapsed = (Date.now() - startedAt) / 1000;
            const pct = Math.min(92, Math.round((elapsed / 90) * 92));
            setGenProgress(pct);
        }, 500);
        return () => { clearInterval(stepInterval); clearInterval(progInterval); };
    }, [busy, genSteps.length]);

    const toggleInterest = (k) => setInterests((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);

    const addLocationFromSearch = async () => {
        if (!search.trim() || visibleLocations.length >= 4) return;
        setErr(""); setSearchBusy(true);
        try {
            const r = await axios.get(`https://nominatim.openstreetmap.org/search`, {
                params: { q: search, format: "json", limit: 1 },
                headers: { "Accept-Language": lang },
            });
            if (r.data && r.data[0]) {
                const short = (r.data[0].display_name || "").split(",")[0].trim();
                setExtraLocations((prev) => [...prev, {
                    lat: parseFloat(r.data[0].lat),
                    lng: parseFloat(r.data[0].lon),
                    label: short || r.data[0].display_name,
                }]);
                setSearch("");
            } else {
                setErr(lang === "it" ? "Località non trovata" : "Location not found");
            }
        } catch {
            setErr("Errore ricerca località");
        } finally { setSearchBusy(false); }
    };

    const setPrimaryFromClick = (ll) => {
        // Only allow map-click to modify the primary chip's coords when the primary is user-added
        // (never modify the immutable borgo coordinates).
        if (primaryIdx === 0 && includeBorgo) return;
        const extraIdx = primaryIdx - (includeBorgo ? 1 : 0);
        setExtraLocations((prev) => prev.map((l, i) => i === extraIdx ? { lat: ll.lat, lng: ll.lng, label: `${ll.lat.toFixed(4)}, ${ll.lng.toFixed(4)}` } : l));
    };

    const removeVisibleLocation = (visibleIdx) => {
        // Cannot remove the borgo chip (uncheck the borgo instead)
        if (includeBorgo && visibleIdx === 0) return;
        const extraIdx = visibleIdx - (includeBorgo ? 1 : 0);
        setExtraLocations((prev) => prev.filter((_, i) => i !== extraIdx));
    };

    const doGenerate = async () => {
        setBusy(true); setErr(""); setGenerated(null);
        try {
            // Start async job
            const { data: kick } = await axios.post(`${API}/itineraries/generate`, {
                borgo: slug,
                filters: {
                    interests, days, mobility,
                    area: {
                        center_lat: primary.lat,
                        center_lng: primary.lng,
                        center_label: primary.label,
                        radius_km: radius,
                        include_borgo: includeBorgo,
                        extra_locations: visibleLocations.filter((_, i) => i !== primaryIdx),
                    },
                    date_from: dateFrom,
                    date_to: dateTo,
                    include_events: includeEvents,
                    adults, children_4_12: children412, children_under_4: childrenU4,
                    budget_level: budgetLevel,
                    origin_city: originCity.trim(),
                    origin_transport: originCity.trim() ? originTransport : "auto_ai",
                    model_key: modelKey,
                    extra_context: extraContextList.map((c) => c.content).join("\n\n---\n\n"),
                    language: lang,
                },
            });
            const jobId = kick.job_id;
            if (!jobId) throw new Error("Nessun job_id ricevuto");

            // Poll every 3s, max 5 minutes
            const started = Date.now();
            let data = null;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                if (Date.now() - started > 5 * 60 * 1000) throw new Error("Timeout generazione (>5min)");
                await new Promise((r) => setTimeout(r, 3000));
                const { data: poll } = await axios.get(`${API}/itineraries/generate/${jobId}`);
                if (poll.status === "done") { data = poll; break; }
                if (poll.status === "error") throw new Error(poll.error || "Errore AI");
                // still pending — loop
            }
            setGenProgress(100);
            setGenerated(data);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Errore generazione");
        } finally { setBusy(false); }
    };

    const doSave = async () => {
        if (!generated) return;
        setBusy(true); setErr("");
        try {
            const payload = {
                tenant_id: generated.tenant_id,
                borgo_slug: generated.borgo_slug,
                title: generated.title,
                subtitle: generated.subtitle,
                filters: generated.filters,
                days: generated.days,
                travel_cost: generated.travel_cost || null,
            };
            await axios.post(`${API}/itineraries`, payload);
            setMsg(t.saved);
            setTimeout(onSaved, 800);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore salvataggio");
        } finally { setBusy(false); }
    };

    const canProceed = useMemo(() => {
        if (step === 0) return interests.length > 0;
        if (step === 1) return days >= 1;
        if (step === 2) return !!mobility;
        if (step === 3) return visibleLocations.length >= 1;
        return true;
    }, [step, interests, days, mobility, visibleLocations.length]);

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-3 py-4 overflow-y-auto" data-testid="itin-wizard">
            <div className="bg-white rounded-3xl w-full max-w-6xl shadow-2xl flex flex-col max-h-[97vh] relative">
                <div className="p-5 border-b border-stone-100 flex items-center justify-between">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-amber-700">AI Wizard</div>
                        <h2 className="font-serif text-xl font-semibold">{t.wizard_title}</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setChatOpen(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow"
                            data-testid="itin-wizard-chat-open"
                            title="Apri Chat AI"
                        >
                            💬 Chat AI
                            {extraContextList.length > 0 && <span className="bg-white text-indigo-700 rounded-full min-w-[18px] h-[18px] text-[10px] font-bold flex items-center justify-center px-1.5">{extraContextList.length}</span>}
                        </button>
                        <button onClick={onClose} className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center" data-testid="itin-wizard-close"><X size={16} /></button>
                    </div>
                </div>

                {!generated && (
                    <div className="px-6 pt-4">
                        <div className="flex gap-2 flex-wrap">
                            {steps.map((s, i) => (
                                <div key={s} className={`flex-1 min-w-[100px] text-[11px] uppercase tracking-widest text-center py-2 rounded-full font-semibold ${i === step ? "bg-amber-100 text-amber-800" : i < step ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-500"}`}>
                                    {i + 1}. {s}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Sticky generation status bar — always visible while generating or when itinerary is ready */}
                <GenerationStatusBar
                    busy={busy}
                    hasResult={!!generated}
                    stepText={genSteps[genStepIdx]}
                    pct={genProgress}
                    modelLabel={modelList.find((m) => m.key === modelKey)?.label || "Claude Sonnet 4.6"}
                    testid="itin-progress"
                />

                <div className="p-6 overflow-y-auto flex-1">
                    {err && <div className="mb-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="itin-err">{err}</div>}
                    {msg && <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2" data-testid="itin-msg">{msg}</div>}

                    {generated ? (
                        <GeneratedPreview
                            generated={generated}
                            center={primary}
                            radius={radius}
                            onSave={doSave}
                            onDiscard={() => setGenerated(null)}
                            busy={busy}
                            t={t}
                            isAdmin={isAdmin}
                            onUpdateStop={(dayIdx, stopIdx, patch) => setGenerated((prev) => prev && ({
                                ...prev,
                                days: prev.days.map((d, di) => di !== dayIdx ? d : { ...d, stops: (d.stops || []).map((s, si) => si !== stopIdx ? s : { ...s, ...patch }) }),
                            }))}
                        />
                    ) : (
                        <>
                            {step === 0 && (
                                <div data-testid="itin-step-interests">
                                    <p className="text-sm text-stone-500 mb-4">Seleziona 1 o più interessi (chip)</p>
                                    <div className="flex flex-wrap gap-2">
                                        {catalog.interests?.map((it) => {
                                            const on = interests.includes(it.key);
                                            return (
                                                <button
                                                    key={it.key}
                                                    onClick={() => toggleInterest(it.key)}
                                                    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${on ? "bg-emerald-600 border-emerald-600 text-white shadow" : "bg-white border-stone-200 text-stone-700 hover:border-emerald-400"}`}
                                                    data-testid={`itin-int-${it.key}`}
                                                >{it[lang] || it.it}</button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {step === 1 && (
                                <div data-testid="itin-step-days">
                                    <label className="block text-sm text-stone-600 mb-3">{t.days_lbl}</label>
                                    <div className="grid grid-cols-5 gap-2">
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((d) => (
                                            <button
                                                key={d}
                                                onClick={() => setDays(d)}
                                                className={`py-3 rounded-xl text-lg font-bold border-2 transition-colors ${days === d ? "bg-amber-600 border-amber-600 text-white" : "bg-white border-stone-200 text-stone-700 hover:border-amber-400"}`}
                                                data-testid={`itin-days-${d}`}
                                            >{d}</button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {step === 2 && (
                                <div data-testid="itin-step-mobility">
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        {catalog.mobility?.map((m) => {
                                            const Icon = MOBILITY_ICON[m.key] || Compass;
                                            const on = mobility === m.key;
                                            return (
                                                <button
                                                    key={m.key}
                                                    onClick={() => setMobility(m.key)}
                                                    className={`p-4 rounded-2xl border-2 flex flex-col items-center gap-2 transition-all ${on ? "bg-emerald-50 border-emerald-500 text-emerald-800 shadow" : "bg-white border-stone-200 hover:border-emerald-300"}`}
                                                    data-testid={`itin-mob-${m.key}`}
                                                >
                                                    <Icon size={22} />
                                                    <div className="text-sm font-semibold">{m[lang] || m.it}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {step === 3 && (
                                <div data-testid="itin-step-area">
                                    <label className="block text-sm font-semibold text-stone-700 mb-1">{t.location_lbl}</label>
                                    <p className="text-xs text-stone-500 mb-3">💡 Clicca ⭐ su un chip per usarlo come centro di ricerca (raggio applicato attorno a lui). Deseleziona &quot;Includi borgo&quot; per non partire da {tenant?.name || "Tadasuni"}.</p>

                                    {visibleLocations.length === 0 && (
                                        <div className="mb-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-3">
                                            ⚠️ Hai deselezionato &quot;Includi il borgo di partenza&quot; ma non hai ancora aggiunto altre località. Aggiungine almeno una qui sotto oppure riattiva il borgo.
                                        </div>
                                    )}

                                    {/* Location chips */}
                                    <div className="flex flex-wrap gap-2 mb-3" data-testid="itin-locations-list">
                                        {visibleLocations.map((loc, idx) => {
                                            const isPrimary = idx === primaryIdx;
                                            const isBorgo = includeBorgo && idx === 0;
                                            return (
                                                <div
                                                    key={idx}
                                                    className={`inline-flex items-center gap-2 pl-3 pr-1 py-1.5 rounded-full text-sm font-medium border-2 transition-colors ${isPrimary ? "bg-amber-100 border-amber-500 text-amber-900" : "bg-stone-100 border-stone-300 text-stone-700 hover:border-amber-300 cursor-pointer"}`}
                                                    data-testid={`itin-location-chip-${idx}`}
                                                    onClick={() => !isPrimary && setPrimaryIdx(idx)}
                                                    title={isPrimary ? "Centro attivo" : "Clicca per usare come centro"}
                                                >
                                                    <MapPin size={12} />
                                                    <span className="max-w-[200px] truncate">{loc.label}</span>
                                                    {isPrimary && <span className="text-[9px] font-bold uppercase tracking-widest bg-amber-600 text-white px-1.5 py-0.5 rounded-full">CENTRO ⭐</span>}
                                                    {isBorgo && !isPrimary && <span className="text-[9px] font-bold uppercase tracking-widest bg-emerald-600 text-white px-1.5 py-0.5 rounded-full">BORGO</span>}
                                                    <div className="flex items-center gap-1">
                                                        {!isPrimary && (
                                                            <button onClick={(e) => { e.stopPropagation(); setPrimaryIdx(idx); }} className="w-6 h-6 rounded-full hover:bg-white/70 text-stone-500 hover:text-amber-700 flex items-center justify-center" title="Usa come centro" data-testid={`itin-location-primary-${idx}`}>⭐</button>
                                                        )}
                                                        {!isBorgo && (
                                                            <button onClick={(e) => { e.stopPropagation(); removeVisibleLocation(idx); }} className="w-6 h-6 rounded-full hover:bg-white/70 text-stone-500 hover:text-red-600 flex items-center justify-center" title="Rimuovi" data-testid={`itin-location-remove-${idx}`}><X size={12} /></button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Add new location via search */}
                                    {visibleLocations.length < 4 && (
                                        <div className="flex gap-2 mb-3">
                                            <div className="flex-1 flex items-center gap-2 border border-stone-200 rounded-lg px-3 py-2 bg-white">
                                                <Search size={14} className="text-stone-400" />
                                                <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLocationFromSearch())} placeholder={t.search} className="flex-1 outline-none text-sm" data-testid="itin-search" />
                                            </div>
                                            <button onClick={addLocationFromSearch} disabled={searchBusy || !search.trim()} className="px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold inline-flex items-center gap-1" data-testid="itin-search-btn">
                                                {searchBusy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} {t.add}
                                            </button>
                                        </div>
                                    )}
                                    {visibleLocations.length >= 4 && (
                                        <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Massimo 4 località di riferimento raggiunto.</div>
                                    )}

                                    {visibleLocations.length > 0 && (
                                        <div className="rounded-2xl overflow-hidden border border-stone-200 mb-3" style={{ height: 460 }}>
                                            <MapContainer center={[primary.lat, primary.lng]} zoom={visibleLocations.length > 1 ? 9 : 11} style={{ height: "100%", width: "100%" }} scrollWheelZoom key={`map-${visibleLocations.length}-${primary.lat}-${primary.lng}`}>
                                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                                                <MapClickPicker onPick={setPrimaryFromClick} />
                                                {visibleLocations.map((loc, idx) => (
                                                    <Marker key={idx} position={[loc.lat, loc.lng]} icon={pinIcon}>
                                                        <Popup>
                                                            <div style={{ minWidth: 140 }}>
                                                                <b>{loc.label}</b>
                                                                <div style={{ fontSize: 10, color: "#78716c", marginTop: 2 }}>{idx === primaryIdx ? "⭐ Centro attivo" : "Località di riferimento"}</div>
                                                            </div>
                                                        </Popup>
                                                    </Marker>
                                                ))}
                                                <Circle center={[primary.lat, primary.lng]} radius={radius * 1000} pathOptions={{ color: "#059669", fillColor: "#10b981", fillOpacity: 0.1 }} />
                                            </MapContainer>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-xs font-semibold text-stone-600 block mb-1">{t.radius}: {radius} km <span className="text-stone-400 font-normal">(attorno a {primary.label})</span></label>
                                            <input type="range" min={5} max={100} step={5} value={radius} onChange={(e) => setRadius(Number(e.target.value))} className="w-full accent-emerald-600" data-testid="itin-radius" />
                                        </div>
                                        <label className="inline-flex items-center gap-2 self-end mb-2">
                                            <input type="checkbox" checked={includeBorgo} onChange={(e) => setIncludeBorgo(e.target.checked)} className="accent-emerald-600" data-testid="itin-include-borgo" />
                                            <span className="text-sm text-stone-700">{t.include_borgo}</span>
                                        </label>
                                    </div>
                                    <div className="text-xs text-stone-500 mt-2">
                                        {visibleLocations.length > 0
                                            ? <>Centro attivo (raggio {radius} km): <b className="text-stone-700">{primary.label}</b></>
                                            : <span className="text-amber-800">Nessun centro attivo — aggiungi almeno una località per procedere.</span>}
                                    </div>
                                </div>
                            )}
                            {step === 4 && (
                                <div data-testid="itin-step-period">
                                    <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/40 p-5 mb-4">
                                        <label className="block text-sm font-semibold text-emerald-900 mb-3">📅 {t.period_lbl}</label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">{t.date_from}</label>
                                                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white outline-none focus:border-emerald-500" data-testid="itin-date-from" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">{t.date_to}</label>
                                                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} min={dateFrom || undefined} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white outline-none focus:border-emerald-500" data-testid="itin-date-to" />
                                            </div>
                                        </div>
                                    </div>

                                    <label className="flex items-start gap-3 p-4 rounded-2xl border-2 border-stone-200 hover:border-amber-400 cursor-pointer transition-colors bg-white">
                                        <input type="checkbox" checked={includeEvents} onChange={(e) => setIncludeEvents(e.target.checked)} className="accent-amber-600 mt-0.5 w-4 h-4" data-testid="itin-include-events" />
                                        <div>
                                            <div className="text-sm font-semibold text-stone-900">🎭 {t.events_toggle}</div>
                                            <div className="text-xs text-stone-500 mt-1">{t.events_hint}</div>
                                        </div>
                                    </label>

                                    {/* Model selector */}
                                    <div className="mt-5">
                                        <ModelSelector models={modelList} value={modelKey} onChange={setModelKey} testid="itin-wizard-model" />
                                    </div>

                                    {/* Extra context chips accumulated from Chat AI */}
                                    {extraContextList.length > 0 && (
                                        <div className="mt-5 rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="text-sm font-semibold text-indigo-900">📝 Contesto extra dalla chat AI ({extraContextList.length})</div>
                                                <button onClick={() => setExtraContextList([])} className="text-[11px] text-indigo-700 underline" data-testid="itin-wizard-ctx-clear">svuota</button>
                                            </div>
                                            <div className="space-y-2">
                                                {extraContextList.map((c, i) => (
                                                    <div key={i} className="text-xs bg-white border border-indigo-100 rounded-xl px-3 py-2 relative pr-8" data-testid={`itin-wizard-ctx-${i}`}>
                                                        <div className="text-[9px] uppercase tracking-widest text-indigo-600 font-bold mb-0.5">{c.model_used || "AI"}</div>
                                                        <div className="text-stone-700 line-clamp-3">{c.content}</div>
                                                        <button onClick={() => setExtraContextList((prev) => prev.filter((_, j) => j !== i))} className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-stone-100 hover:bg-red-100 text-stone-500 hover:text-red-600 flex items-center justify-center"><X size={11} /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Origin city + transport (optional) */}
                                    <div className="mt-5 rounded-2xl border-2 border-sky-200 bg-sky-50/40 p-5">
                                        <label className="block text-sm font-semibold text-sky-900 mb-2">✈️ Luogo di partenza (opzionale — per stimare spese di viaggio A/R)</label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">Da quale città parti?</label>
                                                <input
                                                    type="text"
                                                    value={originCity}
                                                    onChange={(e) => setOriginCity(e.target.value)}
                                                    placeholder="es. Milano, Roma, Berlino…"
                                                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white outline-none focus:border-sky-500"
                                                    data-testid="itin-origin-city"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">Mezzo preferito</label>
                                                <select
                                                    value={originTransport}
                                                    onChange={(e) => setOriginTransport(e.target.value)}
                                                    disabled={!originCity.trim()}
                                                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm bg-white outline-none focus:border-sky-500 disabled:opacity-50"
                                                    data-testid="itin-origin-transport"
                                                >
                                                    <option value="auto_ai">🤖 Scegli tu (miglior mezzo)</option>
                                                    <option value="aereo">✈️ Aereo</option>
                                                    <option value="treno">🚆 Treno</option>
                                                    <option value="nave">🚢 Nave / Traghetto</option>
                                                    <option value="auto">🚗 Auto propria</option>
                                                    <option value="pullman">🚌 Pullman</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="text-[11px] text-stone-500 mt-2">💡 Se compili la città, l&apos;AI calcolerà una stima delle spese di viaggio andata/ritorno (biglietti economy) e proporrà anche mezzi alternativi.</div>
                                    </div>

                                    {/* Participants + Budget */}
                                    <div className="mt-5 rounded-2xl border-2 border-amber-200 bg-amber-50/40 p-5">
                                        <label className="block text-sm font-semibold text-amber-900 mb-3">👥 Partecipanti & Budget</label>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                                            {[
                                                { key: "adults",    label: "Adulti",             value: adults,       set: setAdults,       min: 1, hint: "" },
                                                { key: "kids412",   label: "Bambini 4-12 anni",  value: children412,  set: setChildren412,  min: 0, hint: "sconto ~30%" },
                                                { key: "kidsU4",    label: "Bambini < 4 anni",   value: childrenU4,   set: setChildrenU4,   min: 0, hint: "gratis" },
                                            ].map((p) => (
                                                <div key={p.key} className="bg-white rounded-xl border border-amber-200 p-3">
                                                    <div className="text-xs font-semibold text-stone-600 mb-2">{p.label} {p.hint && <span className="text-stone-400 font-normal">({p.hint})</span>}</div>
                                                    <div className="flex items-center justify-center gap-3">
                                                        <button type="button" onClick={() => p.set(Math.max(p.min, p.value - 1))} className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 text-lg font-bold" data-testid={`itin-${p.key}-dec`}>−</button>
                                                        <span className="text-2xl font-bold text-stone-900 min-w-[2ch] text-center tabular-nums" data-testid={`itin-${p.key}-val`}>{p.value}</span>
                                                        <button type="button" onClick={() => p.set(p.value + 1)} className="w-8 h-8 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-lg font-bold" data-testid={`itin-${p.key}-inc`}>+</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="text-xs font-semibold text-stone-600 mb-2">Livello di spesa</div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                            {[
                                                { key: "economico", label: "💰 Economico",  hint: "10-20€/pasto" },
                                                { key: "medio",     label: "💰💰 Medio",    hint: "25-45€/pasto" },
                                                { key: "alto",      label: "💰💰💰 Alto",   hint: "50-80€/pasto" },
                                                { key: "no_limit",  label: "💎 No limite", hint: "premium" },
                                            ].map((b) => {
                                                const on = budgetLevel === b.key;
                                                return (
                                                    <button
                                                        key={b.key}
                                                        type="button"
                                                        onClick={() => setBudgetLevel(b.key)}
                                                        className={`text-left rounded-xl p-2.5 border-2 transition-all ${on ? "bg-amber-600 border-amber-600 text-white shadow" : "bg-white border-stone-200 text-stone-700 hover:border-amber-400"}`}
                                                        data-testid={`itin-budget-${b.key}`}
                                                    >
                                                        <div className="text-xs font-semibold">{b.label}</div>
                                                        <div className={`text-[10px] mt-0.5 ${on ? "text-amber-100" : "text-stone-500"}`}>{b.hint}</div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {!generated && (
                    <div className="p-4 border-t border-stone-100 flex items-center justify-between">
                        <button onClick={() => (step > 0 ? setStep(step - 1) : onClose())} className="px-4 py-2 rounded-full bg-stone-100 hover:bg-stone-200 text-sm font-semibold" data-testid="itin-back">{step === 0 ? t.skip : t.back}</button>
                        {step < 4 ? (
                            <button disabled={!canProceed} onClick={() => setStep(step + 1)} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50" data-testid="itin-next">{t.next} <ArrowRight size={14} /></button>
                        ) : (
                            <button disabled={busy || !canProceed} onClick={handleGenerateClick} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50" data-testid="itin-generate">
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {busy ? t.generating : t.gen}
                            </button>
                        )}
                    </div>
                )}

                {/* Date-vs-days confirmation dialog */}
                {dateWarnOpen && (
                    <div className="absolute inset-0 z-10 bg-black/40 flex items-center justify-center rounded-3xl p-6" data-testid="itin-datewarn">
                        <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
                            <div className="text-2xl mb-2">⚠️</div>
                            <h3 className="font-serif text-lg font-semibold text-stone-900 mb-2">Il periodo scelto è più lungo dei giorni selezionati</h3>
                            <p className="text-sm text-stone-600 mb-4">Hai selezionato <b>{days} giorni</b> di itinerario, ma il periodo dal <b>{dateFrom}</b> al <b>{dateTo}</b> copre <b>{periodDays} giorni</b>. Vuoi che l&apos;AI generi un itinerario più lungo per coprire tutto il periodo?</p>
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={() => { setDateWarnOpen(false); doGenerate(); }}
                                    className="px-4 py-2 rounded-full bg-stone-100 hover:bg-stone-200 text-sm font-semibold"
                                    data-testid="itin-datewarn-keep"
                                >Mantieni {days} giorni</button>
                                <button
                                    onClick={() => { setDays(suggestedDays); setDateWarnOpen(false); setTimeout(doGenerate, 100); }}
                                    className="px-4 py-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold"
                                    data-testid="itin-datewarn-force"
                                >Forza a {suggestedDays} giorni</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Floating Chat AI trigger removed — now in header */}
            </div>

            {/* Chat AI Drawer */}
            <AIChatDrawer
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                models={modelList}
                modelKey={modelKey}
                onModelKeyChange={setModelKey}
                onAddContext={(content, model_used) => {
                    setExtraContextList((prev) => [...prev, { content, model_used }]);
                }}
                onAddStop={generated ? (dayIdx, stop) => {
                    setGenerated((prev) => prev && ({
                        ...prev,
                        days: prev.days.map((d, di) => di !== dayIdx ? d : { ...d, stops: [...(d.stops || []), stop] }),
                    }));
                } : null}
                days={generated?.days || []}
                borgoSlug={slug}
                lang={lang}
            />
        </div>
    );
}

// ============================ SHARED: GENERATION STATUS BAR ============================
// Sticky bar shown outside the scrollable content of the wizard/editor.
// Renders nothing when idle (no busy + no result).
function GenerationStatusBar({ busy, hasResult, stepText, pct, modelLabel, testid = "gen-progress" }) {
    if (!busy && !hasResult) return null;
    const complete = !busy && hasResult;
    return (
        <div className="px-6 pt-3" data-testid={testid}>
            <div className={`p-3.5 rounded-2xl border-2 ${complete ? "bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-300" : "bg-gradient-to-br from-amber-50 via-orange-50 to-emerald-50 border-amber-200"}`}>
                <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white shadow ${complete ? "bg-emerald-600" : "bg-amber-500"}`}>
                        {complete ? <span className="text-lg leading-none">✓</span> : <Sparkles size={16} className="animate-pulse" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className={`text-[10px] font-bold uppercase tracking-widest ${complete ? "text-emerald-700" : "text-amber-700"}`}>{complete ? "Completato al 100%" : "AI in azione"}</div>
                        <div className="text-xs sm:text-sm font-semibold text-stone-900 truncate" data-testid={`${testid}-step`}>
                            {complete ? "Itinerario generato — rivedi la preview qui sotto" : stepText}
                        </div>
                    </div>
                    <div className={`text-xl font-bold tabular-nums shrink-0 ${complete ? "text-emerald-700" : "text-amber-700"}`} data-testid={`${testid}-pct`}>
                        {complete ? "100%" : `${pct}%`}
                    </div>
                </div>
                <div className="w-full h-2 rounded-full bg-white/70 overflow-hidden mt-2">
                    <div
                        className={`h-full transition-all duration-500 ease-out ${complete ? "bg-emerald-500" : "bg-gradient-to-r from-amber-500 via-orange-500 to-emerald-500"}`}
                        style={{ width: `${complete ? 100 : pct}%` }}
                    />
                </div>
                {!complete && (
                    <div className="mt-1.5 text-[10px] text-stone-500 leading-tight">Sto interrogando <b>{modelLabel}</b> con i tuoi filtri, POI locali del borgo e conoscenza geografica. Tempo stimato: 20-90 secondi.</div>
                )}
            </div>
        </div>
    );
}


// ============================ SHARED: MODEL SELECTOR ============================
const TIER_META = {
    basic:    { label: "Basic",    hint: "💰 basso costo",  color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    advanced: { label: "Advanced", hint: "💰💰 costo medio", color: "bg-amber-100 text-amber-800 border-amber-300" },
    premium:  { label: "Premium",  hint: "💎 costo alto",   color: "bg-rose-100 text-rose-800 border-rose-300" },
};

function ModelSelector({ models, value, onChange, compact = false, testid = "model-selector" }) {
    if (!models || models.length === 0) return null;
    const sel = models.find((m) => m.key === value) || models[0];
    return (
        <div className={compact ? "flex items-center gap-2" : "rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-4"}>
            {!compact && (
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-indigo-900">🤖 Motore AI</div>
                    <div className={`text-[10px] font-bold uppercase tracking-widest border rounded-full px-2 py-0.5 ${TIER_META[sel.tier]?.color || TIER_META.basic.color}`} data-testid={`${testid}-tier`}>
                        {TIER_META[sel.tier]?.label} · {TIER_META[sel.tier]?.hint}
                    </div>
                </div>
            )}
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={`w-full ${compact ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm"} border border-stone-200 rounded-lg bg-white outline-none focus:border-indigo-500`}
                data-testid={testid}
            >
                {models.map((m) => (
                    <option key={m.key} value={m.key}>
                        {TIER_META[m.tier]?.hint?.split(" ")[0] || "💰"} {m.label} — {m.vendor} ({TIER_META[m.tier]?.label})
                    </option>
                ))}
            </select>
            {!compact && (
                <div className="text-[11px] text-stone-500 mt-1.5 leading-snug" data-testid={`${testid}-desc`}>{sel.desc}</div>
            )}
        </div>
    );
}


// ============================ SHARED: AI CHAT DRAWER ============================
// Slide-in from the right. Provides free-form conversation with a chosen model.
// AI responses can be "added as new stop" (via onAddStop) OR "used as extra context"
// (via onAddContext) — both callbacks are optional depending on host (wizard vs editor).
function AIChatDrawer({
    open, onClose, models, modelKey, onModelKeyChange,
    onAddContext, onAddStop, days = [], borgoSlug = "", itineraryId = "", lang = "it",
}) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [pickForMsg, setPickForMsg] = useState(null);   // idx of message being routed to a day

    const send = async () => {
        const text = input.trim();
        if (!text || busy) return;
        setBusy(true);
        const nextMsgs = [...messages, { role: "user", content: text }];
        setMessages(nextMsgs);
        setInput("");
        try {
            const { data } = await axios.post(`${API}/itineraries/chat`, {
                messages: nextMsgs.map((m) => ({ role: m.role, content: m.content })),
                model_key: modelKey,
                borgo_slug: borgoSlug,
                itinerary_id: itineraryId || "",
                lang,
            });
            setMessages((prev) => [...prev, {
                role: "assistant",
                content: data.content,
                model_used: data.model_used,
                tier: data.tier,
            }]);
        } catch (e) {
            setMessages((prev) => [...prev, {
                role: "assistant",
                content: "⚠️ Errore: " + (e?.response?.data?.detail || e.message),
                error: true,
            }]);
        } finally { setBusy(false); }
    };

    // Best-effort parser: extract a structured stop from an AI message with NOME/INDIRIZZO/DESC/COSTO.
    const parseStop = (text) => {
        const grab = (re) => {
            const m = text.match(re);
            return m ? m[1].trim().replace(/^\*+|\*+$/g, "").trim() : "";
        };
        const name = grab(/NOME\s*:?\s*(.+)/i) || grab(/^#{1,3}\s*(.+)$/m) || (text.split("\n")[0] || "").slice(0, 80);
        const address = grab(/INDIRIZZO\s*:?\s*(.+)/i);
        const desc = grab(/DESCRIZIONE\s*:?\s*([\s\S]+?)(?:\n[A-Z]{3,}|$)/i) || text.slice(0, 200);
        const cost = grab(/COSTO(?:\/PERSONA)?\s*:?\s*(.+)/i);
        const durationTxt = grab(/DURATA\s*:?\s*(.+)/i);
        const tip = grab(/TIP\s*:?\s*(.+)/i);
        // Parse cost range "10-25 €" or "10–25€"
        let cMin = null, cMax = null;
        const cm = cost.match(/(\d+(?:[.,]\d+)?)\s*[-–—]\s*(\d+(?:[.,]\d+)?)/);
        if (cm) { cMin = parseFloat(cm[1].replace(",", ".")); cMax = parseFloat(cm[2].replace(",", ".")); }
        else {
            const single = cost.match(/(\d+(?:[.,]\d+)?)/);
            if (single) { cMin = cMax = parseFloat(single[1].replace(",", ".")); }
        }
        const durM = durationTxt.match(/(\d+)/);
        return {
            name: name || "Nuova tappa",
            description: desc || "",
            address: address || "",
            duration_min: durM ? parseInt(durM[1], 10) : 60,
            kind: "poi",
            cost_per_person_min: cMin,
            cost_per_person_max: cMax,
            tips: tip || "",
            lat: null, lng: null,
        };
    };

    const handleAddStop = (mIdx, dayIdx) => {
        const m = messages[mIdx];
        if (!m || !onAddStop) return;
        const stop = parseStop(m.content);
        onAddStop(dayIdx, stop);
        setPickForMsg(null);
    };

    return (
        <div className={`fixed inset-y-0 right-0 z-[60] transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`} data-testid="ai-chat-drawer">
            <div className="w-[92vw] sm:w-[420px] h-full bg-white shadow-2xl border-l border-stone-200 flex flex-col">
                <div className="p-4 border-b border-stone-100 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">💬 Chat AI</div>
                        <div className="text-sm font-semibold text-stone-900">Chiedimi qualsiasi cosa</div>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center" data-testid="ai-chat-close"><X size={14} /></button>
                </div>

                <div className="px-4 pt-3 pb-2 border-b border-stone-100">
                    <ModelSelector models={models} value={modelKey} onChange={onModelKeyChange} compact testid="ai-chat-model" />
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-chat-messages">
                    {messages.length === 0 && (
                        <div className="text-xs text-stone-500 leading-relaxed bg-indigo-50/60 border border-indigo-100 rounded-2xl p-3">
                            <div className="font-semibold text-indigo-900 mb-1">Puoi chiedermi ad esempio:</div>
                            <ul className="list-disc list-inside space-y-1">
                                <li>&quot;Suggeriscimi un ristorante vegano vicino a Tadasuni&quot;</li>
                                <li>&quot;Aggiungi una degustazione di Vernaccia il giorno 2&quot;</li>
                                <li>&quot;Costruisci l&apos;itinerario privilegiando siti nuragici&quot;</li>
                            </ul>
                        </div>
                    )}
                    {messages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-indigo-600 text-white" : m.error ? "bg-red-50 text-red-700 border border-red-200" : "bg-stone-100 text-stone-800"}`} data-testid={`ai-chat-msg-${i}`}>
                                <div className="whitespace-pre-wrap leading-snug" style={{ overflowWrap: "anywhere" }}>{m.content}</div>
                                {m.role === "assistant" && !m.error && (
                                    <>
                                        <div className="text-[9px] uppercase tracking-widest text-stone-500 mt-1 font-semibold">{m.model_used}{m.tier ? ` · ${m.tier}` : ""}</div>
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {onAddStop && days.length > 0 && (
                                                pickForMsg === i ? (
                                                    <>
                                                        <span className="text-[10px] font-semibold text-stone-600 self-center pr-1">a quale giorno?</span>
                                                        {days.map((d, di) => (
                                                            <button key={di} onClick={() => handleAddStop(i, di)} className="text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-full px-2.5 py-1" data-testid={`ai-chat-add-to-day-${i}-${di}`}>G{d.day || di + 1}</button>
                                                        ))}
                                                        <button onClick={() => setPickForMsg(null)} className="text-[11px] text-stone-500 underline">annulla</button>
                                                    </>
                                                ) : (
                                                    <button onClick={() => setPickForMsg(i)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-100 hover:bg-emerald-200 rounded-full px-2.5 py-1" data-testid={`ai-chat-add-stop-${i}`}>
                                                        <Plus size={11} /> Aggiungi come tappa
                                                    </button>
                                                )
                                            )}
                                            {onAddContext && (
                                                <button
                                                    onClick={() => { onAddContext(m.content, m.model_used); }}
                                                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-800 bg-indigo-100 hover:bg-indigo-200 rounded-full px-2.5 py-1"
                                                    data-testid={`ai-chat-add-context-${i}`}
                                                >
                                                    <Sparkles size={11} /> Usa come contesto
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                    {busy && (
                        <div className="flex justify-start">
                            <div className="bg-stone-100 text-stone-500 rounded-2xl px-3 py-2 text-sm flex items-center gap-2">
                                <Loader2 size={13} className="animate-spin" /> Sto pensando…
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-3 border-t border-stone-100">
                    <form
                        onSubmit={(e) => { e.preventDefault(); send(); }}
                        className="flex items-end gap-2"
                    >
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                            rows={2}
                            placeholder="Scrivi la tua richiesta…"
                            className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-indigo-500 resize-none"
                            data-testid="ai-chat-input"
                        />
                        <button
                            type="submit"
                            disabled={busy || !input.trim()}
                            className="w-10 h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white flex items-center justify-center shrink-0"
                            data-testid="ai-chat-send"
                        >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}


// Exported for use in ItinerarioDetailPage
export function TravelCostCard({ travel, adults = 2, k412 = 0, totalPpl = 2 }) {    if (!travel || !travel.from_city) return null;
    const billable = (adults || 0) + (k412 || 0) * 0.7;
    const modeIcon = { aereo: "✈️", treno: "🚆", nave: "🚢", auto: "🚗", pullman: "🚌" };
    const totMin = travel.total_min != null ? Math.round(travel.total_min * billable) : null;
    const totMax = travel.total_max != null ? Math.round(travel.total_max * billable) : null;
    return (
        <div className="mb-5 rounded-2xl bg-gradient-to-br from-sky-50 to-indigo-50 border-2 border-sky-200 p-4" data-testid="itin-travel-cost">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-sky-700 mb-1">🧳 Spese di viaggio A/R da {travel.from_city}</div>
                    {(totMin !== null || totMax !== null) && (
                        <div className="font-serif text-2xl font-bold text-stone-900">
                            {totMin ?? 0}–{totMax ?? 0} € <span className="text-sm text-stone-500 font-normal">totale per {totalPpl} {totalPpl === 1 ? "persona" : "persone"}</span>
                        </div>
                    )}
                    {travel.recommended_mode && (
                        <div className="text-xs text-stone-600 mt-0.5">
                            <b>Consigliato:</b> {modeIcon[travel.recommended_mode] || ""} {travel.recommended_mode}
                        </div>
                    )}
                </div>
                {travel.note && <div className="text-xs text-stone-600 bg-white/60 border border-sky-100 rounded-lg px-3 py-2 max-w-md">💡 {travel.note}</div>}
            </div>
            {Array.isArray(travel.options) && travel.options.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {travel.options.map((opt, i) => (
                        <div key={i} className="bg-white/80 rounded-xl border border-sky-100 px-3 py-2 text-xs" data-testid={`itin-travel-opt-${opt.mode}`}>
                            <div className="font-bold text-stone-800 mb-0.5">{modeIcon[opt.mode] || "🚙"} {opt.mode?.toUpperCase()}</div>
                            <div className="text-stone-700">{opt.cost_per_person_min}–{opt.cost_per_person_max} € <span className="text-stone-500 font-normal">/pers</span></div>
                            {opt.duration_hours ? <div className="text-[10px] text-stone-500 mt-0.5">⏱ {opt.duration_hours}h</div> : null}
                            {opt.note && <div className="text-[10px] text-stone-500 mt-0.5 leading-snug">{opt.note}</div>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function GeneratedPreview({ generated, center, radius, onSave, onDiscard, busy, t, onUpdateStop, isAdmin = false }) {    // Aggregate all stops with day+idx-in-day for numbered markers
    const allStops = (generated.days || []).flatMap((d) => (d.stops || []).map((s, idx) => ({ ...s, day: d.day, idxInDay: idx + 1 })))
        .filter((s) => typeof s.lat === "number" && typeof s.lng === "number");
    const mapCenter = allStops.length > 0 ? [
        allStops.reduce((a, s) => a + s.lat, 0) / allStops.length,
        allStops.reduce((a, s) => a + s.lng, 0) / allStops.length,
    ] : [center.lat, center.lng];
    const totalDays = (generated.days || []).length;
    const [currentDay, setCurrentDay] = useState(1);
    const [mediaEditor, setMediaEditor] = useState(null);   // {di, si, stop}
    const [panorama, setPanorama] = useState(null);
    const [audioBusyKey, setAudioBusyKey] = useState("");
    const [previewAudios, setPreviewAudios] = useState({});   // "di-si" -> blob URL

    const previewAudio = async (di, si, stop) => {
        const key = `${di}-${si}`;
        if (previewAudios[key]) return;   // player already visible
        setAudioBusyKey(key);
        try {
            const text = [stop.name, stop.description, stop.tips].filter(Boolean).join(". ");
            const resp = await adminAxios().post(`/itineraries/audio/preview`,
                { text, lang: generated.filters?.language || "it", borgo_slug: generated.borgo_slug || "" },
                { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([resp.data], { type: "audio/mpeg" }));
            setPreviewAudios((prev) => ({ ...prev, [key]: url }));
        } catch (e) {
            window.alert(e?.response?.data?.detail || "Errore generazione audio");
        } finally { setAudioBusyKey(""); }
    };

    // Budget aggregation: sum cost min/max across all stops × billable participants
    const f = generated.filters || {};
    const adults = f.adults ?? 2;
    const k412 = f.children_4_12 ?? 0;
    const kU4 = f.children_under_4 ?? 0;
    const billablePpl = adults + (k412 * 0.7); // <4 free
    const totalPpl = adults + k412 + kU4;
    let costMin = 0, costMax = 0;
    (generated.days || []).forEach((d) => (d.stops || []).forEach((s) => {
        costMin += (s.cost_per_person_min || 0) * billablePpl;
        costMax += (s.cost_per_person_max || 0) * billablePpl;
    }));
    const hasCosts = costMax > 0;

    // Track which day section is currently in view (sticky breadcrumb)
    useEffect(() => {
        const handler = () => {
            for (let i = totalDays; i >= 1; i--) {
                const el = document.getElementById(`itin-preview-day-${i}`);
                if (el && el.getBoundingClientRect().top < 200) {
                    setCurrentDay(i);
                    return;
                }
            }
            setCurrentDay(1);
        };
        const container = document.querySelector('[data-testid="itin-wizard"] .overflow-y-auto');
        if (container) container.addEventListener("scroll", handler);
        return () => { if (container) container.removeEventListener("scroll", handler); };
    }, [totalDays]);
    return (
        <div data-testid="itin-preview">
            <div className="mb-4">
                <div className="text-xs font-bold uppercase tracking-widest text-amber-700 mb-1">Anteprima</div>
                <h3 className="font-serif text-2xl md:text-3xl font-semibold text-stone-900">{generated.title}</h3>
                {generated.subtitle && <p className="text-stone-600 mt-1">{generated.subtitle}</p>}
                <div className="flex flex-wrap gap-1 mt-2">
                    {(generated.filters?.interests || []).map((iv) => (
                        <span key={iv} className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">{iv}</span>
                    ))}
                </div>
            </div>
            {hasCosts && (
                <div className="mb-5 rounded-2xl bg-gradient-to-br from-emerald-50 to-amber-50 border-2 border-emerald-200 p-4" data-testid="itin-budget-summary">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-1">Budget stimato totale (in loco)</div>
                            <div className="font-serif text-3xl font-bold text-stone-900">{Math.round(costMin)}–{Math.round(costMax)} €</div>
                            <div className="text-[11px] text-stone-500 mt-0.5">per {totalPpl} {totalPpl === 1 ? "persona" : "persone"} ({adults} adulti{k412 ? `, ${k412} bambini 4-12` : ""}{kU4 ? `, ${kU4} bambini <4` : ""})</div>
                        </div>
                        <div className="text-xs text-stone-500 max-w-[280px]">💡 Stime indicative basate su prezzi medi 2026. Bambini 4-12 sconto 30%, bambini &lt;4 gratis.</div>
                    </div>
                </div>
            )}
            <TravelCostCard travel={generated.travel_cost} adults={adults} k412={k412} totalPpl={totalPpl} />
            <div className="rounded-2xl overflow-hidden border border-stone-200 mb-5 shadow-sm" style={{ height: 460 }}>                <MapContainer center={mapCenter} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                    {(generated.days || []).map((d) => (
                        <Polyline
                            key={d.day}
                            positions={(d.stops || []).filter((s) => typeof s.lat === "number").map((s) => [s.lat, s.lng])}
                            pathOptions={{ color: dayColor((d.day || 1) - 1), weight: 3, dashArray: "6 6", opacity: 0.75 }}
                        />
                    ))}
                    {allStops.map((s, i) => (
                        <Marker key={i} position={[s.lat, s.lng]} icon={stopIcon(s.idxInDay, s.day)}>
                            <Popup>
                                <div style={{ minWidth: 180 }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: dayColor((s.day || 1) - 1), textTransform: "uppercase", letterSpacing: 1 }}>Giorno {s.day}</div>
                                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{s.name}</div>
                                    {s.address && <div style={{ fontSize: 11, color: "#78716c" }}>📍 {s.address}</div>}
                                </div>
                            </Popup>
                        </Marker>
                    ))}
                </MapContainer>
            </div>
            <div className="space-y-4 mb-5">
                {totalDays > 1 && (
                    <div className="sticky top-0 z-10 bg-white/95 backdrop-blur -mx-6 px-6 py-2 border-b border-stone-100 flex items-center gap-2" data-testid="itin-preview-breadcrumb">
                        <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Stai leggendo</span>
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-xs font-bold" style={{ backgroundColor: dayColor((currentDay || 1) - 1) }}>
                            Giorno {currentDay} di {totalDays}
                        </span>
                        <div className="flex gap-1 ml-auto">
                            {(generated.days || []).map((d) => (
                                <button
                                    key={d.day}
                                    onClick={() => document.getElementById(`itin-preview-day-${d.day}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                                    className={`w-7 h-7 rounded-full text-white text-[10px] font-bold transition-transform hover:scale-110 ${currentDay === d.day ? "ring-2 ring-offset-1 ring-stone-800" : ""}`}
                                    style={{ backgroundColor: dayColor((d.day || 1) - 1) }}
                                    title={`Giorno ${d.day}`}
                                    data-testid={`itin-breadcrumb-day-${d.day}`}
                                >{d.day}</button>
                            ))}
                        </div>
                    </div>
                )}
                {(generated.days || []).map((d) => (
                    <div key={d.day} id={`itin-preview-day-${d.day}`} className="border-l-4 rounded-r-xl p-4 bg-stone-50 scroll-mt-16" style={{ borderColor: dayColor((d.day || 1) - 1) }}>
                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <div className="font-serif text-lg font-semibold text-stone-900">Giorno {d.day} — {d.title}</div>
                            <div className="text-xs text-stone-500 flex items-center gap-3">
                                {d.total_km ? <span><RouteIcon size={11} className="inline mr-0.5" />{d.total_km} km</span> : null}
                                {d.total_hours ? <span><Clock size={11} className="inline mr-0.5" />{d.total_hours}h</span> : null}
                            </div>
                        </div>
                        {d.summary && <p className="text-sm text-stone-600 mb-3">{d.summary}</p>}
                        <ol className="space-y-2">
                            {(d.stops || []).map((s, i) => {
                                const dIdx = (generated.days || []).findIndex((dd) => dd === d);
                                const mediaCount = (s.photos?.length || 0) + (s.photos_360?.length || 0) + (s.matterport_url ? 1 : 0) + (s.virtual_tour_url ? 1 : 0);
                                return (
                                <li key={i} className="flex gap-3 items-start p-2 rounded-lg hover:bg-stone-50 bg-white">
                                    <div className="w-8 h-8 rounded-full text-white text-sm font-bold flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: dayColor((d.day || 1) - 1) }}>{i + 1}</div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <div className="font-semibold text-stone-900 text-sm">{s.name} {s.duration_min ? <span className="text-xs text-stone-500 font-normal">· {s.duration_min} min</span> : null}</div>
                                            {onUpdateStop && (
                                                <button
                                                    onClick={() => setMediaEditor({ di: dIdx, si: i, stop: s })}
                                                    className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-0.5"
                                                    data-testid={`itin-preview-stop-media-${dIdx}-${i}`}
                                                    title="Aggiungi foto, 360°, tour virtuali"
                                                >🖼️ Media{mediaCount > 0 && <span className="ml-1 bg-emerald-600 text-white rounded-full min-w-[16px] h-[16px] text-[9px] font-bold flex items-center justify-center px-1">{mediaCount}</span>}</button>
                                            )}
                                            {isAdmin && (
                                                <button
                                                    onClick={() => previewAudio(dIdx, i, s)}
                                                    disabled={audioBusyKey === `${dIdx}-${i}`}
                                                    className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-0.5 disabled:opacity-50"
                                                    data-testid={`itin-preview-stop-audio-${dIdx}-${i}`}
                                                    title="Genera anteprima audio (crediti AI)"
                                                >
                                                    {audioBusyKey === `${dIdx}-${i}` ? <Loader2 size={11} className="animate-spin" /> : <span>🔊</span>} Audio
                                                </button>
                                            )}
                                        </div>
                                        {s.address && <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-1"><MapPin size={10} /> {s.address}</div>}
                                        {s.description && <div className="text-xs text-stone-600 mt-1 leading-relaxed">{s.description}</div>}
                                        {s.tips && <div className="text-xs text-emerald-700 mt-1"><b>💡</b> {s.tips}</div>}
                                        {s.travel_to_next && (
                                            <div className="mt-1.5 text-xs text-blue-700 flex items-start gap-1 bg-blue-50 rounded px-2 py-1">
                                                <RouteIcon size={11} className="mt-0.5" />
                                                <span><b>→ Successiva:</b> {s.travel_to_next} {s.distance_to_next_km ? <span className="font-semibold">({s.distance_to_next_km} km)</span> : null}</span>
                                            </div>
                                        )}
                                        {(s.cost_per_person_min != null || s.cost_per_person_max != null) ? (
                                            <div className="mt-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 inline-flex items-center gap-1">
                                                💰 {s.cost_per_person_min === 0 && s.cost_per_person_max === 0 ? "Gratis" : `${s.cost_per_person_min}-${s.cost_per_person_max} €/pers`}
                                                {billablePpl > 0 && s.cost_per_person_max > 0 && <span className="text-stone-500 font-normal">· tot {Math.round(s.cost_per_person_min * billablePpl)}-{Math.round(s.cost_per_person_max * billablePpl)}€</span>}
                                                {s.cost_note && <span className="text-stone-500 font-normal">· {s.cost_note}</span>}
                                            </div>
                                        ) : null}
                                        {previewAudios[`${dIdx}-${i}`] && (
                                            <audio
                                                controls
                                                preload="none"
                                                src={previewAudios[`${dIdx}-${i}`]}
                                                className="w-full mt-2 h-8"
                                                data-testid={`itin-preview-stop-audio-player-${dIdx}-${i}`}
                                            />
                                        )}
                                        <StopMediaGallery
                                            stop={s}
                                            compact
                                            onOpen360={(p) => setPanorama({ url: p.url, caption: p.caption || s.name })}
                                            onOpenPhoto={(p) => window.open(p.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url, "_blank")}
                                        />
                                    </div>
                                </li>
                            );})}
                        </ol>
                    </div>
                ))}
            </div>
            <div className="flex gap-2 justify-end sticky bottom-0 bg-white/95 backdrop-blur border-t border-stone-100 pt-3 -mx-6 px-6">
                <button onClick={onDiscard} className="px-4 py-2 rounded-full bg-stone-100 hover:bg-stone-200 text-sm font-semibold" data-testid="itin-discard">Rigenera</button>
                <button
                    onClick={async () => {
                        try {
                            const resp = await axios.post(`${API}/itineraries/pdf-preview`, {
                                tenant_id: generated.tenant_id, borgo_slug: generated.borgo_slug,
                                title: generated.title, subtitle: generated.subtitle,
                                filters: generated.filters, days: generated.days,
                                travel_cost: generated.travel_cost || null,
                            }, { responseType: "blob" });
                            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
                            window.open(url, "_blank");
                            setTimeout(() => window.URL.revokeObjectURL(url), 30000);
                        } catch (e) { window.alert("Errore PDF: " + (e.message || "")); }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stone-800 hover:bg-stone-900 text-white text-sm font-semibold"
                    data-testid="itin-preview-pdf"
                ><Download size={14} /> PDF</button>
                <button disabled={busy} onClick={onSave} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="itin-save">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {t.save}
                </button>
            </div>

            {mediaEditor && (
                <StopMediaEditor
                    open={true}
                    stop={mediaEditor.stop}
                    allowUpload={isAdmin}
                    borgoSlug={generated?.borgo_slug || ""}
                    onClose={() => setMediaEditor(null)}
                    onSave={(media) => onUpdateStop && onUpdateStop(mediaEditor.di, mediaEditor.si, media)}
                    testidPrefix="itin-preview-media"
                />
            )}
            <Panorama360Viewer
                open={!!panorama}
                url={panorama?.url}
                caption={panorama?.caption}
                onClose={() => setPanorama(null)}
            />
        </div>
    );
}


// ============================ ITINERARY EDITOR (admin) ============================
function ItineraryEditor({ itinerary, onClose, onSaved }) {
    const [title, setTitle] = useState(itinerary.title || "");
    const [subtitle, setSubtitle] = useState(itinerary.subtitle || "");
    const [days, setDays] = useState(() => JSON.parse(JSON.stringify(itinerary.days || [])));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");
    const [regen, setRegen] = useState({ active: false, step: 0, pct: 0 });
    // Chat + model selector
    const [modelList, setModelList] = useState([]);
    const [modelKey, setModelKey] = useState("claude-sonnet-4-6");
    const [chatOpen, setChatOpen] = useState(false);
    // Media editor
    const [mediaEditor, setMediaEditor] = useState(null);   // {di, si, stop}
    const [panorama, setPanorama] = useState(null);          // {url, caption}
    useEffect(() => {
        axios.get(`${API}/itineraries/models`).then((r) => {
            setModelList(r.data.models || []);
            if (r.data.default) setModelKey(r.data.default);
        }).catch(() => {});
    }, []);

    const genSteps = GEN_STEPS.it;

    // Progress animation during regeneration
    useEffect(() => {
        if (!regen.active) return;
        let i = 0;
        const stepInterval = setInterval(() => {
            i = Math.min(i + 1, genSteps.length - 1);
            setRegen((r) => ({ ...r, step: i }));
        }, 5500);
        const startedAt = Date.now();
        const progInterval = setInterval(() => {
            const elapsed = (Date.now() - startedAt) / 1000;
            const pct = Math.min(92, Math.round((elapsed / 90) * 92));
            setRegen((r) => ({ ...r, pct }));
        }, 500);
        return () => { clearInterval(stepInterval); clearInterval(progInterval); };
    }, [regen.active, genSteps.length]);

    const updateStop = (dayIdx, stopIdx, patch) => {
        setDays((prev) => prev.map((d, di) => di !== dayIdx ? d : {
            ...d,
            stops: (d.stops || []).map((s, si) => si !== stopIdx ? s : { ...s, ...patch }),
        }));
    };

    const removeStop = (dayIdx, stopIdx) => {
        setDays((prev) => prev.map((d, di) => di !== dayIdx ? d : {
            ...d,
            stops: (d.stops || []).filter((_, si) => si !== stopIdx),
        }));
    };

    const doSave = async () => {
        setBusy(true); setErr("");
        try {
            await adminAxios().put(`/itineraries/${itinerary.id}`, { title, subtitle, days });
            setMsg("Itinerario salvato!");
            setTimeout(onSaved, 700);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore salvataggio");
        } finally { setBusy(false); }
    };

    const doRegenerate = async () => {
        if (!window.confirm("Rigenerare l'itinerario con l'AI mantenendo lo stesso ID? Le modifiche non salvate andranno perse.")) return;
        setBusy(true); setErr(""); setRegen({ active: true, step: 0, pct: 0 });
        try {
            const { data: kick } = await adminAxios().post(`/itineraries/${itinerary.id}/regenerate`, null, { params: { model_key: modelKey } });
            const jobId = kick.job_id;
            if (!jobId) throw new Error("Nessun job_id ricevuto");
            const started = Date.now();
            let result = null;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                if (Date.now() - started > 5 * 60 * 1000) throw new Error("Timeout generazione (>5min)");
                await new Promise((r) => setTimeout(r, 3000));
                const { data: poll } = await axios.get(`${API}/itineraries/generate/${jobId}`);
                if (poll.status === "done") { result = poll; break; }
                if (poll.status === "error") throw new Error(poll.error || "Errore AI");
            }
            setRegen({ active: true, step: genSteps.length - 1, pct: 100 });
            // Persist via PUT
            await adminAxios().put(`/itineraries/${itinerary.id}`, {
                title: result.title,
                subtitle: result.subtitle,
                days: result.days,
                travel_cost: result.travel_cost || null,
            });
            setTitle(result.title);
            setSubtitle(result.subtitle || "");
            setDays(result.days || []);
            setMsg("Itinerario rigenerato con AI ✨");
            setRegen({ active: false, step: 0, pct: 0 });
            setTimeout(onSaved, 1200);
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message || "Errore rigenerazione");
            setRegen({ active: false, step: 0, pct: 0 });
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-3 py-4 overflow-y-auto" data-testid="itin-editor">
            <div className="bg-white rounded-3xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[95vh] relative">
                <div className="p-5 border-b border-stone-100 flex items-center justify-between">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-amber-700">Editor · Admin</div>
                        <h2 className="font-serif text-xl font-semibold">Modifica itinerario</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setChatOpen(true)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold"
                            title="Apri Chat AI"
                            data-testid="itin-editor-chat-open"
                        >💬 Chat AI</button>
                        <button
                            onClick={doRegenerate}
                            disabled={busy}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-semibold disabled:opacity-50"
                            title="Rigenera con AI (stesso ID)"
                            data-testid="itin-editor-regenerate"
                        >
                            <RefreshCw size={13} className={regen.active ? "animate-spin" : ""} /> Rigenera con AI
                        </button>
                        <button onClick={onClose} className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center" data-testid="itin-editor-close"><X size={16} /></button>
                    </div>
                </div>

                {/* Sticky regenerate progress bar (always visible when regenerating) */}
                <GenerationStatusBar
                    busy={regen.active && regen.pct < 100}
                    hasResult={regen.active && regen.pct >= 100}
                    stepText={genSteps[regen.step]}
                    pct={regen.pct}
                    modelLabel={modelList.find((m) => m.key === modelKey)?.label || "Claude Sonnet 4.6"}
                    testid="itin-editor-progress"
                />

                <div className="p-6 overflow-y-auto flex-1 space-y-4">
                    {err && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="itin-editor-err">{err}</div>}
                    {msg && <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm px-3 py-2" data-testid="itin-editor-msg">{msg}</div>}

                    <ModelSelector models={modelList} value={modelKey} onChange={setModelKey} testid="itin-editor-model" />

                    <div>
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1">Titolo</label>
                        <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:border-emerald-500 outline-none" data-testid="itin-editor-title" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1">Sottotitolo</label>
                        <textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={2} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:border-emerald-500 outline-none" data-testid="itin-editor-subtitle" />
                    </div>

                    {days.map((d, di) => (
                        <div key={di} className="border-l-4 rounded-r-xl p-4 bg-stone-50" style={{ borderColor: dayColor(((d.day || di + 1) - 1)) }}>
                            <div className="font-serif text-lg font-semibold text-stone-900 mb-2">Giorno {d.day || di + 1} — {d.title}</div>
                            <div className="space-y-3">
                                {(d.stops || []).map((s, si) => (
                                    <div key={si} className="bg-white rounded-xl border border-stone-200 p-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-xs font-bold text-stone-500">Tappa {si + 1}</div>
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => setMediaEditor({ di, si, stop: s })}
                                                    className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 font-semibold"
                                                    data-testid={`itin-editor-stop-media-${di}-${si}`}
                                                    title="Gestisci foto, 360°, tour virtuali"
                                                >🖼️ Media {((s.photos?.length || 0) + (s.photos_360?.length || 0) + (s.matterport_url ? 1 : 0) + (s.virtual_tour_url ? 1 : 0)) > 0 && (
                                                    <span className="bg-emerald-600 text-white rounded-full min-w-[16px] h-[16px] text-[9px] font-bold flex items-center justify-center px-1">
                                                        {(s.photos?.length || 0) + (s.photos_360?.length || 0) + (s.matterport_url ? 1 : 0) + (s.virtual_tour_url ? 1 : 0)}
                                                    </span>
                                                )}</button>
                                                <button
                                                    onClick={() => removeStop(di, si)}
                                                    className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                                                    data-testid={`itin-editor-stop-remove-${di}-${si}`}
                                                ><Trash2 size={11} /> Rimuovi</button>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                                            <input value={s.name || ""} onChange={(e) => updateStop(di, si, { name: e.target.value })} placeholder="Nome tappa" className="px-2 py-1.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-emerald-500" data-testid={`itin-editor-stop-name-${di}-${si}`} />
                                            <input value={s.address || ""} onChange={(e) => updateStop(di, si, { address: e.target.value })} placeholder="Indirizzo / zona" className="px-2 py-1.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-emerald-500" data-testid={`itin-editor-stop-address-${di}-${si}`} />
                                        </div>
                                        <textarea
                                            value={s.description || ""}
                                            onChange={(e) => updateStop(di, si, { description: e.target.value })}
                                            rows={2}
                                            placeholder="Descrizione"
                                            className="w-full px-2 py-1.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-emerald-500 mb-2"
                                            data-testid={`itin-editor-stop-desc-${di}-${si}`}
                                        />
                                        <div className="grid grid-cols-3 gap-2">
                                            <div>
                                                <label className="text-[10px] uppercase text-stone-500 font-semibold">Costo min €/pers</label>
                                                <input type="number" min="0" step="0.5" value={s.cost_per_person_min ?? ""} onChange={(e) => updateStop(di, si, { cost_per_person_min: e.target.value === "" ? null : Number(e.target.value) })} className="w-full px-2 py-1 border border-stone-200 rounded text-sm outline-none focus:border-emerald-500" data-testid={`itin-editor-stop-cmin-${di}-${si}`} />
                                            </div>
                                            <div>
                                                <label className="text-[10px] uppercase text-stone-500 font-semibold">Costo max €/pers</label>
                                                <input type="number" min="0" step="0.5" value={s.cost_per_person_max ?? ""} onChange={(e) => updateStop(di, si, { cost_per_person_max: e.target.value === "" ? null : Number(e.target.value) })} className="w-full px-2 py-1 border border-stone-200 rounded text-sm outline-none focus:border-emerald-500" data-testid={`itin-editor-stop-cmax-${di}-${si}`} />
                                            </div>
                                            <div>
                                                <label className="text-[10px] uppercase text-stone-500 font-semibold">Nota costo</label>
                                                <input value={s.cost_note || ""} onChange={(e) => updateStop(di, si, { cost_note: e.target.value })} className="w-full px-2 py-1 border border-stone-200 rounded text-sm outline-none focus:border-emerald-500" data-testid={`itin-editor-stop-cnote-${di}-${si}`} />
                                            </div>
                                        </div>
                                        <StopMediaGallery
                                            stop={s}
                                            compact
                                            onOpen360={(p) => setPanorama({ url: p.url, caption: p.caption || s.name })}
                                            onOpenPhoto={(p) => window.open(p.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url, "_blank")}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="p-4 border-t border-stone-100 flex items-center justify-between">
                    <button onClick={onClose} className="px-4 py-2 rounded-full bg-stone-100 hover:bg-stone-200 text-sm font-semibold" data-testid="itin-editor-cancel">Annulla</button>
                    <button disabled={busy} onClick={doSave} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="itin-editor-save">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salva modifiche
                    </button>
                </div>

                {/* Floating Chat AI trigger removed — now in header */}
            </div>

            {/* Chat AI Drawer — can add a new stop directly into any day */}
            <AIChatDrawer
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                models={modelList}
                modelKey={modelKey}
                onModelKeyChange={setModelKey}
                onAddContext={null}
                onAddStop={(dayIdx, stop) => {
                    setDays((prev) => prev.map((d, di) => di !== dayIdx ? d : {
                        ...d,
                        stops: [...(d.stops || []), stop],
                    }));
                    setMsg("Tappa aggiunta al giorno " + ((days[dayIdx]?.day) || (dayIdx + 1)) + "! Ricordati di salvare.");
                    setTimeout(() => setMsg(""), 4000);
                }}
                days={days}
                borgoSlug={itinerary.borgo_slug}
                itineraryId={itinerary.id}
                lang="it"
            />

            {/* Media editor (per-stop) */}
            {mediaEditor && (
                <StopMediaEditor
                    open={true}
                    stop={mediaEditor.stop}
                    allowUpload={true}
                    borgoSlug={itinerary.borgo_slug}
                    onClose={() => setMediaEditor(null)}
                    onSave={(media) => {
                        setDays((prev) => prev.map((d, di) => di !== mediaEditor.di ? d : {
                            ...d,
                            stops: (d.stops || []).map((s, si) => si !== mediaEditor.si ? s : { ...s, ...media }),
                        }));
                        setMsg("Media aggiornati. Ricordati di salvare l'itinerario.");
                        setTimeout(() => setMsg(""), 3500);
                    }}
                    testidPrefix="itin-editor-media"
                />
            )}

            {/* 360° viewer */}
            <Panorama360Viewer
                open={!!panorama}
                url={panorama?.url}
                caption={panorama?.caption}
                onClose={() => setPanorama(null)}
            />
        </div>
    );
}
